import _ from 'lodash';

import { AbstractLimitationModel } from '../AbstractLimitation';
import { SelectOptions } from 'components/commonType';
import { DefaultSelectItemComponentModel, ItemSetting } from '../SelectItemComponent';
import { FireableUpdateEventListener, UpdateEventListener } from 'utils/UpdateEventListener';
import i18n from 'i18n';
import { flattenOptions } from 'utils/SelectOptionsUtils';
export interface SelectComponentModel extends AbstractLimitationModel {
  readonly event: UpdateEventListener<SelectComponentModel>;
  readonly singleSelection: boolean;
  readonly searchPlaceHolder: string;
  readonly itemSetting: ItemSetting;
  readonly state: SelectComponentState;
  readonly contentPlaceholderI18nKey: string;
  readonly contentUnitI18nKey: string;
  modelMap: {[key: string]: DefaultSelectItemComponentModel};
  onChange: (selectedOptions: SelectOptions[]) => void;
  getSelectItemModel: (itemData: SelectOptions, listRef: any) => DefaultSelectItemComponentModel;
  getSelectItemModelByValue: (value) => DefaultSelectItemComponentModel;
  onSearch (keyword: string): void;
  setData (data): void;
  updateCachedItemModels (modelMap, listRef: any);
  setState (state: SelectComponentState, needRefreshValue?: boolean): void;
  onUnmount (eventHandler): void;
}

export type SelectComponentProps = {
  model: SelectComponentModel;
};

export type SelectComponentState = {
  readonly optionsToShow: SelectOptions[];
  readonly searchString: string;
  readonly selectedValue?: SelectOptions[];
};

export class DefaultSelectComponentModel implements SelectComponentModel {

  event: FireableUpdateEventListener<SelectComponentModel> =
    new FireableUpdateEventListener<SelectComponentModel>();
  modelMap: {[key: string]: DefaultSelectItemComponentModel} = {};
  searchString: string = '';
  optionsToShow: SelectOptions[];

  constructor (
    public name: string,
    public singleSelection: boolean,
    public itemSetting: ItemSetting,
    public searchPlaceHolder: string,
    public onChange: (selectedOptions: SelectOptions[]) => void,
    public data?: SelectOptions[],
    public value?: SelectOptions[],
    customInputModelGetter?: any,
    private disable?: boolean,
    public contentPlaceholderI18nKey: string = i18n.t<string>('limitation.placeholders.categoryTip'),
    public contentUnitI18nKey: string = 'limitation.classificationTotal'
  ) {
    this.optionsToShow = data ? data : [];
  }

  get state () {
    return {
      optionsToShow: this.optionsToShow,
      searchString: this.searchString,
      selectedValue: this.value
    };
  }

  setState (state: SelectComponentState, needRefreshValue: boolean = false) {
    this.optionsToShow = state.optionsToShow;
    if (needRefreshValue) {
      this.value = state.selectedValue;
    }
    if (this.searchString !== state.searchString) {
      this.searchString = state.searchString;
      this.onSearch(state.searchString);
    }
  }

  findParentValues (node: SelectOptions, targetValue: string | number, path: (string | number)[] = []) {
    if (node.value === targetValue) {
      return path;
    }

    const newPath = [...path, node.value];
    if (node.options && node.options.length > 0) {
      for (const child of node.options) {
        const result = this.findParentValues(child, targetValue, newPath);
        if (result) {
          return result;
        }
      }
    }

    return null;
  }

  findParentValuesOfItem (itemData: SelectOptions): (string | number)[] {
    const allOptions = this.data ? this.data : [];
    for (const option of allOptions) {
      const result = this.findParentValues(option, itemData.value);
      if (result) {
        return result;
      }
    }
    return [];
  }

  getSelectItemModel (itemData: SelectOptions, listRef: { current: null | HTMLDivElement }) {
    const parentValues = this.findParentValuesOfItem(itemData);
    const cachedModel = this.getSelectItemModelByValue(itemData.value);
    const hasValueSelect = this.value && this.value.length > 0;
    const itemShouldDisable = this.disable || (this.singleSelection && !!hasValueSelect);
    if (cachedModel) {
      cachedModel.setData(itemData);
      cachedModel.updateProps(
        itemShouldDisable,
        this.value,
        parentValues,
        (selectedData) => {
          this.onItemChange(itemData, selectedData);
        },
        (itemData: SelectOptions) => this.getSelectItemModel(itemData, listRef)
      );
      return cachedModel;
    }
    const model = new DefaultSelectItemComponentModel(
      itemData,
      this.singleSelection,
      this.itemSetting,
      (selectedData) => {
        this.onItemChange(itemData, selectedData);
      },
      listRef,
      (itemData: SelectOptions) => this.getSelectItemModel(itemData, listRef),
      itemShouldDisable,
      this.value,
      parentValues
    );
    this.modelMap[itemData.value] = model;
    return model;
  }

  getSelectItemModelByValue (value: string | number): DefaultSelectItemComponentModel {
    return this.modelMap[value];
  }

  setData (data: SelectOptions[]) {
    this.data = data;
  }

  updateCachedItemModels (modelMap: {[key: string]: DefaultSelectItemComponentModel}, listRef: { current: null | HTMLDivElement }) {
    this.modelMap = modelMap;
    Object.values(this.modelMap).forEach(cachedModel => {
      const parentValues = this.findParentValuesOfItem(cachedModel.data);
      const hasValueSelect = this.value && this.value.length > 0;
      const itemShouldDisable = this.singleSelection && !!hasValueSelect;
      cachedModel.updateProps(
        itemShouldDisable,
        this.value,
        parentValues,
        (selectedData) => {
          this.onItemChange(cachedModel.data, selectedData);
        },
        (itemData) => this.getSelectItemModel(itemData, listRef)
      );
    });
    this.updateState();
  }

  removeSelectedSubOptionsOfItemData (selectedArray: SelectOptions[], itemData: SelectOptions) {
    const allSubOptions = itemData.options ? flattenOptions(itemData.options) : [];
    _.remove(selectedArray, (selectedOption) => {
      return _.findIndex(allSubOptions, (option: SelectOptions) => selectedOption.value === option.value) >= 0;
    });
  }

  onItemChange (itemData: SelectOptions, selectedData: SelectOptions) {
    const result = !this.value ? [selectedData] : this.value.concat(selectedData);
    const selectedItemIsCategoryItem = selectedData.value === itemData.value;
    selectedItemIsCategoryItem && this.removeSelectedSubOptionsOfItemData(result, itemData);
    this.value = [...result];
    this.onChange(result);
    this.updateState();
  }

  getTranslateLabel (value: string | number): string {
    return i18n.t<string>(`${this.itemSetting.i18nPrefix}.${value.toString().toLowerCase().replace(/-|\s/g, '_')}`);
  }

  onSearch = (keyword: string): void => {
    this.searchString = keyword;
    const dataList = _.defaultTo(this.data, []);
    if (keyword === '') {
      this.optionsToShow = dataList;
      Object.values(this.modelMap).forEach(model => {
        model.setOpen(false);
      });
      this.updateState();
      return;
    }
    const keywordIgnoreCase = keyword.toLowerCase();

    const getLabelOfOption = (option: SelectOptions) => {
      return this.itemSetting.i18nPrefix ?
        this.getTranslateLabel(option.value) :
        _.defaultTo(option.label, option.value.toString());
    };

    const setupCachedModel = (value: string | number, open = true) => {
      const cachedModel = this.getSelectItemModelByValue(value);
      cachedModel && cachedModel.setOpen(open);
    };

    const filterOptions = (
      option: SelectOptions
    ): SelectOptions | undefined => {
      const dataLabel = getLabelOfOption(option);
      if (dataLabel.toLowerCase().includes(keywordIgnoreCase)) {
        setupCachedModel(option.value);
        return { ...option };
      }

      const subOptionsToShow = _.compact(
        _.defaultTo(option.options, []).map(filterOptions)
      );
      if (subOptionsToShow.length > 0) {
        setupCachedModel(option.value);
        return { ...option, options: subOptionsToShow };
      }
    };

    this.optionsToShow = _.compact(dataList.map(filterOptions));

    this.updateState();
  }

  onUnmount (eventHandler?: number) {
    eventHandler && this.event.remove(eventHandler);
    this.modelMap = {};
  }

  updateState () {
    this.event.fireEvent(this);
  }
}
